(ns nosco.richtext.plugins.embedded-images
  (:require
   ["prosemirror-state" :as prosemirror-state]
   [clojure.string :as string]
   [nosco.richtext.plugins.placeholder :as plugins-placeholder]
   [nosco.richtext.uploads :as uploads]
   [nosco.richtext.util :as richtext-util]
   [nosco.util :as util]))

(def ^{:private true} convert-embedded-images-plugin-key (prosemirror-state/PluginKey. "convert-embedded-images-plugin-key"))

(defn- convert-embedded-images-plugin-state-apply [^js tr state]
  (.getMeta tr convert-embedded-images-plugin-key))

(defn- convert-embedded-images-plugin-node-handler
  "Add the node to image-nodes-atom when node is a figure node and node's child is an embedded (data-url) image node"
  [image-nodes-atom ^js node pos parent index]
  (when (= "figure" (.-type.name node))
    (let [content (.-content.content node)
          ^js maybe-image (first content)]
      (when (and (= "image" (.-type.name maybe-image))
                 (string/starts-with? (.-attrs.src maybe-image) "data:"))
        (swap! image-nodes-atom
               conj
               {:node node
                :pos pos
                :data-url (.-attrs.src maybe-image)
                ;; Delete any images which don't contain embedded image data
                :delete (not (string/starts-with? (.-attrs.src maybe-image) "data:image/"))}))))
  true)

(defn- convert-embedded-images-plugin-append-transaction
  "Check for \"paste\" or \"drop\" transactions. When such a transaction is found, walk the
  nodes that have been added to the editor by this transaction and add all the embedded (data-url) image
  nodes found to the state of the plugin for further processing."
  [transactions old-state ^js new-state]
  (let [image-nodes-atom (atom [])]
    (doseq [^js transaction transactions]
      (when-let [transaction-meta (.-meta transaction)]
        (when (or (.-paste transaction-meta) (= "drop" (.-uiEvent transaction-meta)))
          (.descendants
           (.-doc new-state)
           (partial convert-embedded-images-plugin-node-handler image-nodes-atom)))))
    (when-not (empty? @image-nodes-atom)
      (let [tr (.-tr new-state)]
        (.setMeta tr convert-embedded-images-plugin-key @image-nodes-atom)
        tr))))

(defn- data-url-to-file [data-url filename]
  (let [arr (.split data-url ",")
        mime (aget (.match (aget arr 0) #":(.*?);") 1)
        bstr (js/atob (aget arr 1))
        n (.-length bstr)
        u8arr (js/Uint8Array. n)]
    (doseq [i (range 0 n)]
      (aset u8arr i (.charCodeAt bstr i)))
    (js/File. #js [u8arr] filename #js {:type mime})))

(defn- convert-embedded-images-plugin-view-update
  "When embedded (data-url) images are found, upload them and replace
  them by their uploaded version"
  [^js view _prev-state]
  (doseq [{:keys [^js node pos data-url delete]}
          (.getState convert-embedded-images-plugin-key
                     (.-state view))]
    (.dispatch view
               (.. view -state -tr
                   (delete pos (+ pos (.-nodeSize node)))))
    (when-not delete
      ;; TODO should use correct extension
      (let [file (data-url-to-file data-url "imagename.png")
            upload-url-p (uploads/upload-images [file])

            upload-dimensions-p
            (.then upload-url-p richtext-util/get-image-dimensions)

            placeholder-id-p
            (-> (richtext-util/get-image-dimensions data-url)
                (.then
                 (fn [[width height]]
                   (let [[width' height'] (uploads/image-initial-dimensions
                                           width
                                           height)]
                     (plugins-placeholder/insert-image-placeholder!
                      view
                      pos
                      data-url
                      width'
                      height')))))]
        (-> (js/Promise.all [upload-url-p upload-dimensions-p placeholder-id-p])
            (util/delay-promise 500)
            (.then
             (fn [[url [width height] placeholder-id]]
               (let [[width' height'] (uploads/image-initial-dimensions
                                       width
                                       height)
                     image-node (.. view -state -schema -nodes -image
                                    (create #js {:src url
                                                 :width width'
                                                 :height height'}))
                     figure-node (.. view -state -schema -nodes -figure
                                     (create #js {:data-align "center"}
                                             image-node))]
                 (plugins-placeholder/replace-image-placeholder!
                  view
                  placeholder-id
                  figure-node)))))))))

;; Embeddded images can be inserted in the editor by drag and dropping them from another browser tab
(defn convert-embedded-images-plugin
  "Look for embedded (data-url) images added by \"paste\" and \"drop\" transactions and replace them by an uploaded image.
  Removes any images which don't contain image data, which can sometimes occur when pasting other file types."
  []
  (prosemirror-state/Plugin.
   #js {:key convert-embedded-images-plugin-key
        :state #js {:init (fn [] [])
                    :apply convert-embedded-images-plugin-state-apply}
        :appendTransaction convert-embedded-images-plugin-append-transaction
        :view (fn [_] #js {:update convert-embedded-images-plugin-view-update
                           :destroy (fn [])})}))
